var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { isArray, isString, omit } from 'lodash';
import moment from 'moment';
import React from 'react';
import EdsDatePicker from '@ellucian/react-design-system/core/es/DatePicker';
import EdsStandaloneDatePicker from '@ellucian/react-design-system/core/es/StandaloneDatePicker';
import Constants from '../../../helpers/Constants';
class DatePicker extends React.Component {
    constructor(props) {
        super(props);
        this.getDate = () => this.state.date ? this.state.date.toDate() : null;
        this.getEndYear = () => this.props.yearsAfter ? moment().year() + this.props.yearsAfter : moment().year() + 20;
        this.getInitialMonth = () => (this.state.date ? this.state.date : moment()).toDate();
        this.getMaxDate = () => {
            const { rangeEnd, rangeEndPeriod } = this.props;
            return (rangeEnd && rangeEndPeriod ? moment().startOf(rangeEndPeriod).add(rangeEnd, rangeEndPeriod)
                : moment().add(100, 'years')).toDate();
        };
        this.getMinDate = () => {
            const { rangeStart, rangeStartPeriod } = this.props;
            return (rangeStart && rangeStartPeriod ? moment().startOf(rangeStartPeriod).subtract(rangeStart, rangeStartPeriod)
                : moment().subtract(100, 'years')).toDate();
        };
        this.getModifiers = () => {
            let modifiers;
            const { dateModifiers } = this.props;
            if (dateModifiers) {
                modifiers = {};
                const objectKeys = Object.keys(dateModifiers);
                if (objectKeys.length > 0) {
                    objectKeys.forEach(key => {
                        if (modifiers) {
                            if (isArray(dateModifiers[key])) {
                                modifiers[key] = this.mapDates(dateModifiers[key]);
                            }
                            else if (isString(dateModifiers[key])) {
                                modifiers[key] = moment(dateModifiers[key], Constants.dateFormatMoment, true).toDate();
                            }
                            else {
                                modifiers[key] = dateModifiers[key];
                            }
                        }
                    });
                }
            }
            return modifiers;
        };
        this.getStartYear = () => this.props.yearsBefore ? moment().year() - this.props.yearsBefore : moment().year() - 100;
        this.getValidatedDate = (format, value) => {
            let dateValue = null;
            if (value) {
                const momentObj = moment(value, format, true);
                this.isValid = momentObj.isValid();
                if (this.isValid) {
                    dateValue = momentObj;
                }
            }
            return dateValue;
        };
        this.mapDates = (dates) => {
            let mappedDates;
            if (dates) {
                mappedDates = [];
                mappedDates = dates.map(day => {
                    if (isString(day)) {
                        const momentObj = moment(day, Constants.dateFormatMoment, true);
                        return momentObj.toDate();
                    }
                    return day;
                });
            }
            return mappedDates;
        };
        this.onBlur = () => {
            const { format, id, onChange } = this.props;
            if (this.inputDate && this.inputDate.value && onChange) {
                const dateValue = this.getValidatedDate(format.toUpperCase(), this.inputDate.value);
                if (dateValue === null) {
                    this.setState({
                        date: dateValue
                    });
                    onChange('', id, this.isValid);
                }
            }
        };
        this.onDateChange = (date, event, modifiers) => {
            const { id, onChange } = this.props;
            if (date) {
                const dateValue = moment(date);
                this.setState({
                    date: dateValue
                });
                this.isValid = dateValue.isValid();
                if (onChange) {
                    onChange(dateValue.format(Constants.dateFormatMoment), id, this.isValid, modifiers, event);
                }
            }
            else {
                this.isValid = true;
                this.setState({
                    date: null
                });
                if (onChange) {
                    onChange('', id, this.isValid, modifiers, event);
                }
            }
        };
        this.isValid = true;
        moment.locale(this.props.culture);
        this.state = this.getInitialState(props.value);
    }
    getInitialState(value) {
        return {
            date: this.getValidatedDate(Constants.dateFormatMoment, value)
        };
    }
    componentDidMount() {
        const { id, onChange } = this.props;
        this.inputDate = document.getElementById(`${id}-input`);
        if (!this.isValid && onChange) {
            onChange('', id, true);
        }
    }
    componentDidUpdate(prevProps) {
        if (this.props.value !== prevProps.value) {
            const value = this.props.value;
            this.setState({
                date: this.getValidatedDate(Constants.dateFormatMoment, value)
            });
        }
    }
    render() {
        const _a = this.props, { dateModifiersClassNames, dateModifiersStyles, disabledDates, flip, format, PopperProps, selectedDates, useLastFunctionalYear, withMonthYearSelect, variant } = _a, rest = __rest(_a, ["dateModifiersClassNames", "dateModifiersStyles", "disabledDates", "flip", "format", "PopperProps", "selectedDates", "useLastFunctionalYear", "withMonthYearSelect", "variant"]);
        const popperPropsOverrides = flip ? Object.assign({ modifiers: [
                {
                    name: 'flip',
                    enabled: true
                }
            ] }, PopperProps) : PopperProps;
        if (variant === 'standalone') {
            const standaloneDatePicker = (React.createElement(EdsStandaloneDatePicker, Object.assign({ defaultMonth: this.getInitialMonth(), disabledDays: this.mapDates(disabledDates), endYear: useLastFunctionalYear ? Constants.lastFunctionalYear : this.getEndYear(), maxDate: this.getMaxDate(), minDate: this.getMinDate(), modifiers: this.getModifiers(), modifiersClassNames: dateModifiersClassNames, modifiersStyles: dateModifiersStyles, selectedDays: this.mapDates(selectedDates), startYear: this.getStartYear(), value: this.getDate(), withMonthYearSelect: withMonthYearSelect ? withMonthYearSelect : false, onBlur: this.onBlur, onChange: this.onDateChange }, omit(rest, [
                'dateModifiers',
                'disabledDates',
                'rangeEnd',
                'rangeEndPeriod',
                'rangeStart',
                'rangeStartPeriod',
                'selectedDates',
                'value',
                'yearsAfter',
                'yearsBefore',
                'onChange'
            ]))));
            return (React.createElement(React.Fragment, null, standaloneDatePicker));
        }
        else {
            const datePicker = (React.createElement(EdsDatePicker, Object.assign({ disabledDays: this.mapDates(disabledDates), endYear: useLastFunctionalYear ? Constants.lastFunctionalYear : this.getEndYear(), format: format, fullWidth: true, defaultMonth: this.getInitialMonth(), maxDate: this.getMaxDate(), minDate: this.getMinDate(), modifiers: this.getModifiers(), modifiersClassNames: dateModifiersClassNames, modifiersStyles: dateModifiersStyles, PopperProps: popperPropsOverrides, selectedDays: this.mapDates(selectedDates), startYear: this.getStartYear(), value: this.getDate(), withMonthYearSelect: withMonthYearSelect ? withMonthYearSelect : true, onBlur: this.onBlur, onDateChange: this.onDateChange }, omit(rest, [
                'culture',
                'dateModifiers',
                'disabledDates',
                'flip',
                'rangeEnd',
                'rangeEndPeriod',
                'rangeStart',
                'rangeStartPeriod',
                'selectedDates',
                'value',
                'yearsAfter',
                'yearsBefore',
                'onChange'
            ]))));
            return (React.createElement(React.Fragment, null, datePicker));
        }
        ;
    }
}
export default DatePicker;
